/**
 * ForgotPasswordPage.tsx — Supabase Auth password reset (Task #34).
 * Calls supabase.resetPasswordForEmail directly; no server round-trip needed.
 */

import { useState } from 'react';
import { User, ArrowLeft } from 'lucide-react';
import WorkRoamerBrand from '@/components/WorkRoamerBrand';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#060f1f', border: '1.5px solid rgba(59,130,246,0.22)',
  borderRadius: '8px', color: '#e2e8f0', fontSize: '14px', padding: '13px 14px 13px 44px',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};

function GlowDivider() {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.5) 100%)' }} />
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', margin: '0 3px', flexShrink: 0, boxShadow: '0 0 8px 2px rgba(59,130,246,0.7)' }} />
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(59,130,246,0.5) 0%, transparent 100%)' }} />
    </div>
  );
}

export default function ForgotPasswordPage() {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const { resetPassword } = useSupabaseAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error: authError } = await resetPassword(email.trim());
      if (authError) {
        setError(authError.message || 'Something went wrong. Please try again.');
        return;
      }
      setSent(true);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
      padding: '20px 16px', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: '430px', boxSizing: 'border-box' as const,
        padding: 'clamp(24px,6vw,40px) clamp(16px,5.5vw,32px) clamp(20px,4vw,28px)',
        overflow: 'hidden' as const,
        background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)',
        borderRadius: '20px', border: '1.5px solid rgba(59,130,246,0.45)',
        boxShadow: '0 0 40px rgba(37,99,235,0.30), 0 0 80px rgba(37,99,235,0.12)',
        display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
      }}>
        <WorkRoamerBrand width={300} style={{ marginBottom: 4 }} />
        <p style={{ margin: '6px 0 22px', color: '#8fa8c8', fontSize: '13px', textAlign: 'center' }}>
          Reset your access credentials.
        </p>
        <GlowDivider />

        {sent ? (
          <div style={{ width: '100%', marginTop: '28px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px', background: 'rgba(59,130,246,0.15)', border: '1.5px solid rgba(59,130,246,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="24" height="24" fill="none" stroke="#3b82f6" strokeWidth="2.2" viewBox="0 0 24 24">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700', margin: '0 0 8px' }}>Reset Link Sent</p>
            <p style={{ color: '#8fa8c8', fontSize: '13px', margin: '0 0 20px', lineHeight: 1.5 }}>
              If <strong style={{ color: '#cbd5e1' }}>{email}</strong> is registered, you'll receive a reset link shortly.
            </p>
            <a href="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#3b82f6', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
              <ArrowLeft size={15} /> Back to Login
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#3b82f6', fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.16em', textTransform: 'uppercase' as const, marginBottom: '8px' }}>
                WorkRoamer ID
              </label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email address" style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)')}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')}
                />
              </div>
              <p style={{ color: '#4b6fa8', fontSize: '11px', margin: '6px 0 0', lineHeight: 1.4 }}>
                Enter your registered email. We'll send a secure reset link via Supabase Auth.
              </p>
            </div>

            {error && <p style={{ color: '#f87171', fontSize: '12px', textAlign: 'center', margin: 0 }}>{error}</p>}

            <button type="submit" disabled={loading} style={{
              width: '100%',
              background: loading ? 'rgba(37,99,235,0.45)' : 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 45%, #3b82f6 100%)',
              border: 'none', borderRadius: '8px', color: '#ffffff',
              fontSize: '13px', fontWeight: '800', letterSpacing: '0.16em', textTransform: 'uppercase' as const,
              padding: '16px 24px', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: loading ? 'none' : '0 4px 18px rgba(37,99,235,0.45)',
              fontFamily: 'inherit', opacity: loading ? 0.65 : 1,
            }}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>

            <p style={{ textAlign: 'center', margin: 0 }}>
              <a href="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#3b82f6', fontSize: '12px', textDecoration: 'none' }}>
                <ArrowLeft size={13} /> Back to Login
              </a>
            </p>
          </form>
        )}

        <div style={{ borderTop: '1px solid rgba(59,130,246,0.12)', width: '100%', paddingTop: '16px', marginTop: 28 }}>
          <p style={{ color: '#2a3d58', fontSize: '11px', margin: 0, textAlign: 'center' }}>
            © 2026 <strong style={{ color: '#3a5070' }}>WorkRoamer™</strong>{' '}•{' '}Built for people who work from anywhere
          </p>
        </div>
      </div>
    </div>
  );
}
